import { styled } from "goober";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "../Tooltip";
import {
  BADGE_GAP,
  rowsForBadgeWidths,
} from "./MembersSettings/lib/badgeOverflow";
import { PillBadge } from "./shared";

// No badge is narrower than a pill's 16px of horizontal padding.
const MIN_BADGE_WIDTH = 16;

const badgeCapacity = (width: number) =>
  Math.floor((width + BADGE_GAP) / (MIN_BADGE_WIDTH + BADGE_GAP));

const Root = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: ${BADGE_GAP}px;
  align-items: center;
  width: 100%;
  max-width: 100%;
  max-height: 20px;
  overflow: hidden;
  min-width: 0;
`;

const MeasureLayer = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  flex-wrap: nowrap;
  gap: ${BADGE_GAP}px;
  align-items: center;
  white-space: nowrap;
`;

const MeasureItem = styled("span")`
  display: inline-flex;
`;

export function OverflowBadges<T>({
  items,
  keyFn,
  renderBadge,
  overflowTooltip,
  extraOverflowCount = 0,
  extraOverflowLabel,
}: {
  items: readonly T[];
  keyFn: (item: T) => string;
  renderBadge: (item: T) => ReactNode;
  overflowTooltip: (hiddenItems: readonly T[]) => string;
  extraOverflowCount?: number;
  extraOverflowLabel?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  // Mount only badges that could fit, so long lists stay cheap in every row.
  const measured = items.slice(0, badgeCapacity(containerWidth));
  const minOverflow = Math.max(
    1,
    items.length - measured.length + extraOverflowCount
  );
  const maxOverflow = items.length + extraOverflowCount;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const update = () => setContainerWidth(root.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;
    if (root.clientWidth === 0) {
      setVisibleCount(badgeCapacity(window.innerWidth));
      return;
    }
    if (containerWidth === 0) return;

    const badgeWidths = Array.from(
      measure.querySelectorAll<HTMLElement>("[data-overflow-badge]")
    ).map((el) => el.offsetWidth);
    const countWidths = new Map(
      Array.from(
        measure.querySelectorAll<HTMLElement>("[data-overflow-count]")
      ).map((el) => [Number(el.dataset.overflowCount), el.offsetWidth])
    );

    let next = 0;
    for (let count = badgeWidths.length; count >= 0; count--) {
      const total = items.length - count + extraOverflowCount;
      const widths =
        total > 0
          ? [...badgeWidths.slice(0, count), countWidths.get(total) ?? 0]
          : badgeWidths;
      if (rowsForBadgeWidths(widths, containerWidth) <= 1) {
        next = count;
        break;
      }
    }

    setVisibleCount(next);
  }, [items, extraOverflowCount, containerWidth, measured.length]);

  const visible = items.slice(0, visibleCount ?? 0);
  const hidden = items.slice(visibleCount ?? 0);
  // Until measured, show nothing rather than a count covering every badge.
  const totalOverflow =
    visibleCount === null ? 0 : hidden.length + extraOverflowCount;

  return (
    <Root ref={rootRef}>
      {visible.map((item) => (
        <span key={keyFn(item)}>{renderBadge(item)}</span>
      ))}
      {totalOverflow > 0 && (
        <Tooltip
          content={[
            hidden.length > 0 ? overflowTooltip(hidden) : "",
            extraOverflowLabel ?? "",
          ]
            .filter(Boolean)
            .join(", ")}
        >
          <PillBadge interactive>+{totalOverflow}</PillBadge>
        </Tooltip>
      )}
      <MeasureLayer ref={measureRef}>
        {measured.map((item) => (
          <MeasureItem key={keyFn(item)} data-overflow-badge>
            {renderBadge(item)}
          </MeasureItem>
        ))}
        {Array.from({ length: maxOverflow - minOverflow + 1 }, (_, i) => {
          const n = minOverflow + i;
          return (
            <MeasureItem key={`count-${n}`} data-overflow-count={n}>
              <PillBadge>+{n}</PillBadge>
            </MeasureItem>
          );
        })}
      </MeasureLayer>
    </Root>
  );
}
